import {
  createContext,
  useContext,
  type ReactNode,
  useState,
  useCallback,
} from 'react';
import {
  Experiment,
  type ExperimentUser,
  type ExperimentClient,
} from '@amplitude/experiment-js-client';
import type { Featureflag } from '../constants/featureFlags';
import { getEnv, getEnvBoolean } from '../../utils/env';

const amplitudeProxyDomainName = getEnv('AMPLITUDE_PROXY_DOMAIN_NAME');

const areAllFeatureFlagsEnabled = getEnvBoolean('ALL_FEATURE_FLAGS_ENABLED');

interface ExperimentContextType {
  getFeatureVariant: (key: string) => unknown;
  getFeatureValue: <T>(key: string, defaultValue: T) => T | true;
  reloadAllFlagsForUser: (
    userProperties: ExperimentUser['user_properties']
  ) => Promise<void>;
  isFlagEnabled: (key: Featureflag, enableOnVariant?: string) => boolean;
  featuresAvailable: Featureflag[];
  setFeaturesAvailable: (features: Featureflag[]) => void;
  isAvailableToCurrentUser: boolean;
  setIsAvailableToCurrentUser: (isVisible: boolean) => void;
  isLoadingFlags: boolean;
}

const ExperimentContext = createContext<ExperimentContextType>({
  getFeatureVariant: () => null,
  getFeatureValue: (_, defaultValue) => defaultValue,
  reloadAllFlagsForUser: () => Promise.resolve(undefined),
  isFlagEnabled: () => false,
  featuresAvailable: [],
  setFeaturesAvailable: () => undefined,
  isAvailableToCurrentUser: false,
  setIsAvailableToCurrentUser: () => undefined,
  isLoadingFlags: true,
});

interface ExperimentProviderProps {
  children: ReactNode;
}

const amplitudeApiKey = getEnv('AMPLITUDE_KEY');

const initializeExperiment = () => {
  if (typeof window === 'undefined') return;
  if (!amplitudeApiKey) return;

  try {
    return Experiment.initializeWithAmplitudeAnalytics(amplitudeApiKey!, {
      fetchOnStart: true,
      serverUrl: `https://${amplitudeProxyDomainName}/api-lab`,
      flagsServerUrl: `https://${amplitudeProxyDomainName}/flags`,
    });
  } catch (error) {
    console.error('Failed to initialize Amplitude Experiment:', error);
  }
};

const client: ExperimentClient | undefined = initializeExperiment();

export function ExperimentProvider({ children }: ExperimentProviderProps) {
  const [featuresAvailable, setFeaturesAvailable] = useState<Featureflag[]>([]);
  const [isAvailableToCurrentUser, internalSetIsAvailableToCurrentUser] =
    useState(false);
  const [isLoadingFlags, setIsLoadingFlags] = useState(true);

  const setIsAvailableToCurrentUser = useCallback((isVisible: boolean) => {
    internalSetIsAvailableToCurrentUser(isVisible);
    setIsLoadingFlags(false);
  }, []);

  const getFeatureVariant = useCallback((key: string) => {
    if (!client) return null;

    try {
      return client.variant(key);
    } catch (error) {
      console.error(`Error getting feature variant ${key}:`, error);
      return null;
    }
  }, []);

  const isFeatureEnabled = useCallback(
    (key: string, enabledOnVariant = key) => {
      if (!client) return false;
      if (areAllFeatureFlagsEnabled) return true;
      try {
        const variant = client.variant(key);
        return variant?.key === enabledOnVariant;
      } catch (error) {
        console.error(`Error getting feature value ${key}:`, error);
        return false;
      }
    },
    []
  );

  const getFeatureValue = useCallback(
    <T,>(key: string, defaultValue: T): T | true => {
      if (!client) return defaultValue;
      if (areAllFeatureFlagsEnabled) return true;
      try {
        const variant = client.variant(key);
        return (variant?.value as T) ?? defaultValue;
      } catch (error) {
        console.error(`Error getting feature value ${key}:`, error);
        return defaultValue;
      }
    },
    []
  );

  const reloadAllFlagsForUser = useCallback(
    async (userProperties: ExperimentUser['user_properties']) => {
      if (!client) throw new Error('Experiment client is not initialized');
      try {
        await client.fetch({
          user_properties: userProperties,
        });
      } catch (error) {
        console.error('Failed to fetch flags:', error);
      }
    },
    []
  );

  const value = {
    getFeatureVariant,
    getFeatureValue,
    reloadAllFlagsForUser,
    isFlagEnabled: isFeatureEnabled,
    featuresAvailable,
    setFeaturesAvailable,
    isAvailableToCurrentUser,
    setIsAvailableToCurrentUser,
    isLoadingFlags,
  };

  return (
    <ExperimentContext.Provider value={value}>
      {children}
    </ExperimentContext.Provider>
  );
}

export function useExperiment() {
  return useContext(ExperimentContext);
}
